/**
 * Launcher V2 PR C U5 — the one place `sequence-readiness.ts`'s pure
 * derivation gets wired to real data (R3, R6). A `.ts` hook rather than
 * inline in each `.tsx` call site: `QualifyStep`'s `RungRow`,
 * `SequencesStep`'s `SegmentationRailCard`, and `ReviewStep`'s `ReviewRow`
 * all need the identical "resolve the mapped sequence's channel/flowId from
 * the library, then its steps from its own detail query" wiring, and
 * duplicating that three times would be three places to keep in sync
 * (team convention: reuse before you build).
 *
 * Uses the SAME query key (`sequenceDetailQueryKey(sequenceId)`)
 * `SequencesStep.tsx`'s own `SequenceEditorShell` already fetches with —
 * react-query dedupes/shares the cache entry, so this never doubles a
 * request for a sequence already open in the editor, and a rung's badge
 * elsewhere updates the moment that query resolves.
 *
 * `sequence-readiness.ts`'s own contract governs the conservative-by-default
 * behavior here: while the mapped sequence's library summary or its steps
 * haven't loaded yet, this reports `'not_ready'` rather than a guessed
 * `'ready'` — see that module's doc comment on `SequenceReadinessInput`.
 *
 * Round-3c QA fixes, H3 — also returns `reasons` (via
 * `rungReadinessReasons`, the same derivation `rungReadiness` itself is now
 * built from) so the three badge call sites can render WHY a rung is not
 * ready instead of a bare "Not ready". Returned as a small object rather
 * than changing this hook's existing return shape to a tuple, so every call
 * site keeps compiling if it only destructures `readiness`.
 *
 * Play isolation (plans/2026-09-25-002-playbooks-play-isolation-lean-plan.md,
 * U5) — takes no `playId`: a play-owned sequence is its own identity
 * (Decision 12), so the sequence read this hook wires up never varies by
 * play. Every caller (`QualifyStep`'s `RungRow`, `SequencesStep`'s
 * `SegmentationRailCard`, `ReviewStep`'s `ReviewRow`) reads that same
 * un-scoped sequence detail regardless of whether it's mounted in the
 * playbook doorway or inside a play.
 */
import { useQuery } from '@tanstack/react-query';

import { useApi } from '../_shared/hooks';
import { getSequence } from './api-client';
import { sequenceDetailQueryKey } from './query-keys';
import type { PlaybooksLibraryResponse, SequenceMapping } from './schemas';
import {
  rungReadiness,
  rungReadinessReasons,
  type ReadinessRequirement,
  type RungReadiness,
  type SequenceReadinessInput,
} from './sequence-readiness';
import { useGongReplyStepOrders } from './use-gong-reply-step-orders';

export interface RungReadinessResult {
  readiness: RungReadiness;
  reasons: ReadinessRequirement[];
}

export function useRungReadiness(
  mapping: SequenceMapping | null,
  library: PlaybooksLibraryResponse | undefined,
): RungReadinessResult {
  const { apiFetch } = useApi();
  const sequenceSummary = mapping
    ? library?.sequences.find((s) => s.id === mapping.sequenceId)
    : undefined;

  const sequenceQuery = useQuery({
    queryKey: sequenceDetailQueryKey(mapping?.sequenceId ?? ''),
    queryFn: () => getSequence(apiFetch, mapping!.sequenceId),
    enabled: !!mapping,
  });

  // QA round 7, U4 (item 6) — called unconditionally (rules of hooks); the
  // hook itself answers the "unknown shape" empty set for a non-Gong channel
  // or an unbound sequence, with no request issued.
  const gongReplyStepOrders = useGongReplyStepOrders(sequenceSummary?.channel, sequenceSummary?.flowId ?? null);

  const sequenceInput: SequenceReadinessInput | undefined = sequenceSummary
    ? {
        channel: sequenceSummary.channel,
        flowId: sequenceSummary.flowId,
        // Playbooks QA round 5, R5-4 — the sequence's OWN sender, live, not
        // the rung's stale copy. See `sequence-readiness.ts`'s doc comment on
        // `SequenceReadinessInput.sender` / `effectiveSender` in
        // `sender-choice.ts`.
        sender: sequenceSummary.sender,
        steps: sequenceQuery.data?.head?.steps,
        // QA round 7, U4 (item 6) — `useGongReplyStepOrders` exposes NO
        // "loaded" flag: it returns the SAME empty set both while the bound
        // flow's shape is still loading and once it has genuinely loaded
        // with zero reply steps, so this caller cannot tell those two cases
        // apart from the returned value alone. For any non-`gong_engage`
        // channel that ambiguity doesn't matter (the readiness derivation
        // never consults `replyStepOrders` there). For `gong_engage`, passing
        // an empty set through would read as "loaded, no replies" and could
        // suppress a real reply-step-1 exemption while the flow's shape is
        // still in flight — so an empty set is passed through only once we
        // have SOME positive evidence a reply exists (`size > 0`); otherwise
        // `undefined` is passed, which `rungReadinessReasons` treats as
        // "unknown, skip the subject reason entirely." The accepted cost: a
        // `gong_engage` rung whose flow is truly, permanently reply-free at
        // ordinal 1 never shows the "Not ready — step 1 subject" badge
        // through this wiring — conservative-by-design, never a false
        // "Not ready" from an in-flight fetch. Fixing that would mean adding
        // an `isLoading`/`isFetched` flag to `useGongReplyStepOrders` itself,
        // which is out of this unit's file list.
        replyStepOrders:
          sequenceSummary.channel !== 'gong_engage' || gongReplyStepOrders.size > 0
            ? gongReplyStepOrders
            : undefined,
      }
    : undefined;

  return {
    readiness: rungReadiness({ mapping }, sequenceInput),
    reasons: rungReadinessReasons({ mapping }, sequenceInput),
  };
}
